import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'

import Modal from 'components/Modal'
import SearchBox from 'components/SearchBox'
import DoubleToggleButton from 'components/DoubleToggleButton'
import SelectableItemsList from 'components/SelectableItemsList'
import CheckboxIndeterminate from 'components/CheckboxIndeterminate'

import { getItems } from 'store/Items/selector'
import {
  clearLocationSettings,
  clearMenuSettings,
  clearServiceTypes,
  clearServiceTypesWithItems,
  setServiceTypesWithItem,
} from 'store/RevenueCenters/actions'
import { clearEditRevenueCenter } from 'store/RevenueCenters/actions'
import {
  getAssignedItemsToMenu,
  getServiceTypesWithItems,
} from 'store/RevenueCenters/selector'
import { itemsCategoryFilterOptions } from 'resources'
import { selectableListItems } from 'utils/selectableList'

const Items = () => {
  const { menuUuid } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()

  const assignedItemsToMenu = useSelector((state) =>
    getAssignedItemsToMenu(state, menuUuid)
  )
  const transformedAssignedItemsToMenuUuids =
    assignedItemsToMenu?.map(({ uuid }) => uuid)
  const serviceTypesWithItems = useSelector(getServiceTypesWithItems)
  const allItems = useSelector(getItems)

  // states
  const [itemsUuids, setItemsUuids] = useState(
    serviceTypesWithItems[menuUuid] ??
      transformedAssignedItemsToMenuUuids
  )
  const [selectedFilterOption, setSelectedFilterOption] =
    useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  // displayed items on the list
  const {
    filteredItemsBySelectedFilterOption,
    checkedItemsWithinFilteredItemsUuids,
    itemsSelectedNotDisplayed,
    isCheckAll,
    isIndeterminate,
    isDisabled,
  } = selectableListItems(
    allItems,
    searchQuery,
    selectedFilterOption,
    itemsUuids
  )

  // handlers
  const handleCheckAllItems = () => {
    // if some items are selected, select all
    if (
      checkedItemsWithinFilteredItemsUuids?.length <
      filteredItemsBySelectedFilterOption?.length
    ) {
      setItemsUuids([
        ...filteredItemsBySelectedFilterOption.map(
          (item) => item.uuid
        ),
        ...itemsSelectedNotDisplayed,
      ])
      return {
        checked: true,
        indeterminate: false,
      }
      // if all items are selected, unselect all
    } else if (
      checkedItemsWithinFilteredItemsUuids?.length ===
      filteredItemsBySelectedFilterOption?.length
    ) {
      setItemsUuids(itemsSelectedNotDisplayed)
      return {
        checked: false,
        indeterminate: false,
      }
      // if some items are selected, select all
    } else if (checkedItemsWithinFilteredItemsUuids?.length === 0) {
      return {
        checked: false,
        indeterminate: true,
      }
    }
  }

  const handleCheckboxChange = (itemUuid) => {
    if (itemsUuids?.includes(itemUuid)) {
      setItemsUuids(itemsUuids?.filter((uuid) => uuid !== itemUuid))
    } else {
      setItemsUuids([...itemsUuids, itemUuid])
    }
  }

  const handleSave = () => {
    dispatch(
      setServiceTypesWithItem({
        ...serviceTypesWithItems,
        [menuUuid]: itemsUuids,
      })
    )
    history.goBack()
  }

  const onClickReturn = () => history.goBack()

  const onCloseModal = () => {
    history.push(`/revenue-centers`)
    dispatch(clearServiceTypes())
    dispatch(clearLocationSettings())
    dispatch(clearMenuSettings())
    dispatch(clearEditRevenueCenter())
    dispatch(clearServiceTypesWithItems())
  }

  return (
    <Modal
      title="Items"
      isReturnIcon
      onClose={onCloseModal}
      onClickReturn={onClickReturn}
      className="-narrow"
    >
      <div className="modal-body">
        <div className="item-settings">
          <section className="selectable-items">
            <SearchBox
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search..."
            />
            <div className="selectable-items__searchbox">
              <DoubleToggleButton
                options={itemsCategoryFilterOptions}
                selectedUuid={selectedFilterOption}
                onChange={setSelectedFilterOption}
              />
            </div>
            <CheckboxIndeterminate
              handleChange={handleCheckAllItems}
              checked={isCheckAll}
              indeterminate={isIndeterminate}
              label="All items"
              className={
                isCheckAll ? '-checkbox-indeterminate-checked' : ''
              }
              disabled={isDisabled}
            />
            <SelectableItemsList
              handleChange={handleCheckboxChange}
              items={filteredItemsBySelectedFilterOption}
              selectedItems={itemsUuids}
            />
          </section>
          <footer className="selectable-items__footer">
            <button
              className="btn -primary selectable-items__submit-btn"
              onClick={handleSave}
            >
              Save
            </button>
          </footer>
        </div>
      </div>
    </Modal>
  )
}

export default Items
